import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useState } from "react"

interface UseMergeEntitiesProps<T> {
	mergeUrl: string
	getId: (item: T) => number
	onSuccess: (primary: T) => void
}

export function useMergeEntities<T>({
	mergeUrl,
	getId,
	onSuccess,
}: UseMergeEntitiesProps<T>) {
	const xhr = useXHR()
	const [resetKey, setResetKey] = useState(0)

	const merge = async (
		primary: T,
		duplicates: T[],
		confirm: () => Promise<boolean>,
		entityLabel: string
	) => {
		try {
			if (!(await confirm())) return

			await xhr.post(mergeUrl, {
				primary: getId(primary),
				duplicates: duplicates.map(getId),
			})

			const mergeMore = window.confirm(
				`Do you want to merge more duplicate ${entityLabel}s?`
			)

			if (mergeMore) {
				setResetKey((k) => k + 1)
			} else {
				onSuccess(primary)
			}
		} catch (error) {
			const e = error as Error
			alert(e.message || "Something went wrong")
		}
	}

	return { merge, resetKey }
}

export function isPrimaryRemoved<T>(
	primary: T | undefined,
	duplicates: T[],
	getId: (item: T) => string | number
) {
	if (!primary) return false

	return duplicates.every((item) => getId(item) !== getId(primary))
}

export function useDebounce<T>(value: T, delay = 500): T {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}
